import React, { useState, useLayoutEffect } from "react"
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  DialogTitle,
  TextField,
  Button,
  CircularProgress,
  IconButton,
  Grid,
  Autocomplete,
} from "@mui/material"
import i18n from "../../../i18n"
import CloseIcon from "@mui/icons-material/Close"
import PropTypes from "prop-types"
import CompanyModal from "../CompanyModal"
import TagField from "common/TagField"
import {
  useCreateContactTag,
  useDeleteContactTag,
  useCreateCompany,
  useEditCompany,
} from "../hooks/useContacts"

const initialState = {
  title: "",
  firstname: "",
  lastname: "",
  telephone: "",
  role: "",
  email: "",
  mobile: "",
  website: "",
  companyId: "",
}

const ContactModal = ({
  open,
  onClose,
  onSave,
  contact,
  companies,
  contactTags,
  organizationId,
  queryClient,
}) => {
  const [mode, setMode] = useState("create")
  const [fields, setFields] = useState(initialState)
  const [companyModalOpen, setCompanyModalOpen] = useState(false)
  const [isOpeningCompanyModal, setIsOpeningCompanyModal] = useState(false)
  const [editingCompany, setEditingCompany] = useState(null)
  const [selectedTags, setSelectedTags] = useState([])
  const [errors, setErrors] = useState({})
  const [isSaving, setIsSaving] = useState(false)
  const createTagMutation = useCreateContactTag()
  const deleteTagMutation = useDeleteContactTag()
  const createCompanyMutation = useCreateCompany()
  const editCompanyMutation = useEditCompany()

  useLayoutEffect(() => {
    if (open) {
      const isEditing = Boolean(contact && contact.PK && contact.SK)
      setMode(isEditing ? "edit" : "create")
      setFields(
        contact
          ? {
              title: contact.title || "",
              role: contact.role || "",
              firstname: contact.firstname || "",
              lastname: contact.lastname || "",
              email: contact.email || "",
              website: contact.website || "",
              mobile: contact.mobile || "",
              telephone: contact.telephone || "",
              companyId: (() => {
                if (!contact.SK) return ""
                const [companyPart, contactPart] = contact.SK.split("#")
                if (companyPart && companyPart === contactPart) return ""
                return companyPart || ""
              })(),
            }
          : initialState,
      )
      setSelectedTags(
        contact?.tags
          ?.map(simpleTag =>
            contactTags.find(
              tag =>
                tag.properties.name === simpleTag.tag_name &&
                tag.properties.color === simpleTag.tag_color,
            ),
          )
          .filter(Boolean) || [],
      )
      setErrors({})
      setIsSaving(false)
    }
  }, [open, contact])

  const handleChange = e => {
    const { name, value } = e.target
    setFields(prev => ({ ...prev, [name]: value }))
  }

  const handleCompanyChange = (event, newValue) => {
    setFields(prev => ({ ...prev, companyId: newValue ? newValue.SK : "" }))
  }
  const handleOpenCompanyModal = (company = null) => {
    setIsOpeningCompanyModal(Boolean(company))
    setEditingCompany(company)
    setCompanyModalOpen(true)
  }

  const handleCloseCompanyModal = () => {
    setCompanyModalOpen(false)
    setEditingCompany(null)
    setIsOpeningCompanyModal(false)
  }

  const handleSaveCompany = companyData => {
    const companyToEdit = editingCompany

    if (companyToEdit) {
      editCompanyMutation.mutate(
        {
          organizationId,
          companyData: { ...companyData, SK: companyToEdit.SK },
        },
        {
          onSuccess: savedCompany => {
            const detailCompanyId = savedCompany?.SK || companyToEdit?.SK
            queryClient.invalidateQueries({ queryKey: ["companies"] })
            if (detailCompanyId) {
              queryClient.invalidateQueries({
                queryKey: ["company", organizationId, detailCompanyId],
              })
            }
            setFields(prev => ({ ...prev, companyId: savedCompany.SK }))
            setCompanyModalOpen(false)
            setEditingCompany(null)
          },
          onError: error => {
            console.error("Error al editar compañía:", error)
          },
        },
      )
    } else {
      // Crear nueva compañía
      createCompanyMutation.mutate(
        { organizationId, companyData },
        {
          onSuccess: savedCompany => {
            queryClient.invalidateQueries({ queryKey: ["companies"] })
            if (savedCompany?.SK) {
              queryClient.invalidateQueries({
                queryKey: ["company", organizationId, savedCompany.SK],
              })
            }
            setFields(prev => ({ ...prev, companyId: savedCompany.SK }))
            setCompanyModalOpen(false)
            setEditingCompany(null)
          },
          onError: error => {
            console.error("Error al guardar compañía:", error)
          },
        },
      )
    }
  }

  const handleCreateTag = (tagName, tagColor) => {
    createTagMutation.mutate(
      {
        organizationId,
        tagName,
        tagColor,
        category: "F",
        objectType: "contact",
      },
      {
        onSuccess: newTag => {
          queryClient.invalidateQueries({
            queryKey: ["contactTags", organizationId],
          })
          console.log("Tag creado:", newTag)
        },
        onError: error => {
          console.error("Error al crear tag:", error)
        },
      },
    )
  }

  const handleDeleteTag = tag => {
    deleteTagMutation.mutate(
      {
        organizationId,
        tagSKs: [tag.SK],
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({
            queryKey: ["contactTags", organizationId],
          })
          setSelectedTags(
            selectedTags.filter(selectedTag => selectedTag.SK !== tag.SK),
          )
          console.log("Tag eliminado:", tag)
        },
        onError: error => {
          console.error("Error al eliminar tag:", error)
        },
      },
    )
  }

  const handleSave = e => {
    if (!e.target.checkValidity()) return
    e.preventDefault()

    const newErrors = {}
    if (!fields.firstname.trim()) {
      newErrors.firstname = true
    }
    setErrors(newErrors)

    if (Object.keys(newErrors).length > 0) return

    const transformedTags = selectedTags.map(tag => ({
      tag_name: tag.properties.name,
      tag_color: tag.properties.color,
    }))
    const { companyId, ...contactData } = fields

    const fullContactData =
      mode === "edit"
        ? {
            ...contactData,
            PK: contact.PK,
            SK: contact.SK,
            tags: transformedTags,
          }
        : {
            ...contactData,
            tags: transformedTags,
          }
    setIsSaving(true)
    onSave(fullContactData, companyId || undefined)
    setErrors({})
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {mode === "edit"
          ? i18n.t("contacts.editContact")
          : i18n.t("contacts.createContact")}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: theme => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <form onSubmit={handleSave} id="contact-form">
          <Typography variant="subtitle1">
            {i18n.t("contacts.company")}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <Autocomplete
                options={companies || []}
                getOptionLabel={option => option.companyName || ""}
                value={
                  companies
                    ? companies.find(
                        company => company.SK === fields.companyId,
                      ) || null
                    : null
                }
                onChange={handleCompanyChange}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={i18n.t("contacts.companyName")}
                  />
                )}
                sx={{ mb: 2 }}
              />
            </Grid>
            <Grid item xs={6}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  gap: 1,
                  mt: 1,
                }}
              >
                {!fields.companyId ? (
                  <Button
                    onClick={() => handleOpenCompanyModal(null)}
                    variant="outlined"
                    size="large"
                  >
                    {i18n.t("contacts.newCompany")}
                  </Button>
                ) : (
                  <Button
                    onClick={() =>
                      handleOpenCompanyModal(
                        companies.find(c => c.SK === fields.companyId),
                      )
                    }
                    variant="outlined"
                    size="large"
                    disabled={isOpeningCompanyModal}
                    sx={{ width: 172, height: 42 }}
                  >
                    {isOpeningCompanyModal ? (
                      <CircularProgress size={20} />
                    ) : (
                      i18n.t("contacts.editCompany")
                    )}
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>

          <Typography variant="subtitle1">
            {i18n.t("contacts.personalData")}
          </Typography>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={3}>
              <TextField
                label={i18n.t("contacts.contactTitle")}
                name="title"
                value={fields.title}
                onChange={handleChange}
                fullWidth
                autoFocus
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label={i18n.t("contacts.firstName")}
                name="firstname"
                value={fields.firstname}
                onChange={handleChange}
                fullWidth
                error={!!errors.firstname}
                required
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label={i18n.t("contacts.lastName")}
                name="lastname"
                value={fields.lastname}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={3}>
              <TextField
                label={i18n.t("contacts.phone")}
                name="telephone"
                value={fields.telephone}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={4}>
              <TextField
                label={i18n.t("contacts.role")}
                name="role"
                value={fields.role}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={i18n.t("contacts.mobile")}
                name="mobile"
                value={fields.mobile}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                label={i18n.t("contacts.email")}
                name="email"
                value={fields.email}
                onChange={handleChange}
                fullWidth
                type="email"
              />
            </Grid>
          </Grid>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={6}>
              <TextField
                label={i18n.t("contacts.website")}
                name="website"
                value={fields.website}
                onChange={handleChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TagField
                options={contactTags}
                selectedTags={selectedTags}
                onChange={setSelectedTags}
                onCreateTag={handleCreateTag}
                onDeleteTag={handleDeleteTag}
                label={i18n.t("contacts.tags")}
              />
            </Grid>
          </Grid>
        </form>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 3 }}>
        <Button onClick={onClose} color="secondary" variant="outlined">
          {i18n.t("common.cancel")}
        </Button>
        <Button
          type="submit"
          form="contact-form"
          color="branded"
          variant="contained"
          disabled={isSaving}
          sx={{ width: 120, height: 36 }}
        >
          {isSaving ? <CircularProgress size={20} /> : i18n.t("common.save")}
        </Button>
      </DialogActions>
      <CompanyModal
        open={companyModalOpen}
        onClose={handleCloseCompanyModal}
        onSave={handleSaveCompany}
        organizationId={organizationId}
        company={editingCompany}
        contactFullName={`${fields.firstname || ""} ${fields.lastname || ""}`.trim()}
        onReady={() => setIsOpeningCompanyModal(false)}
      />
    </Dialog>
  )
}

ContactModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  contact: PropTypes.object,
  companies: PropTypes.array.isRequired,
  contactTags: PropTypes.array,
  organizationId: PropTypes.string.isRequired,
  queryClient: PropTypes.object.isRequired,
}

export default ContactModal
